import * as React from "react";

import {
  TusePolicyDocumentsPoll,
  TusePolicyDocumentsPollArgs,
  usePolicyDocumentsPoll,
} from "./usePolicyDocumentsPoll";
import {
  TuseQuoteDocumentsPoll,
  TuseQuoteDocumentsPollArgs,
  useQuoteDocumentsPoll,
} from "./useQuoteDocumentsPoll";

import { Block } from "baseui/block";
import { FileList } from "kanopi-components/DocumentDownloader";
import { HeadingMedium } from "kanopi-components/typography";
import { Placeholder } from "kanopi-components/Placeholder";
import { QuestionFlowTypeKey } from "kw/components/QuestionFlow/QuestionFlowComponent";
import { useGlobal } from "kanopi-components/useGlobal";

export type TStatelessDocumentList = {
  documents: unknown;
  loading?: boolean;
  label?: string;
};

export type TDocumentPollList = {
  riskProfileId: string;
  label?: string;
  shouldPollDocuments: boolean;
  quoteId?: string;
  globalDataKey?: string;
};

export type TDocumentPollListExtraArgs = {
  useDocumentsPoll: (
    args: TuseQuoteDocumentsPollArgs | TusePolicyDocumentsPollArgs
  ) => TuseQuoteDocumentsPoll | TusePolicyDocumentsPoll;
};

export const StatelessDocumentList: React.FC<TStatelessDocumentList> = ({
  documents,
  loading = false,
  label = "Important documents",
}) => {
  return (
    <Placeholder loading={loading}>
      {documents?.length > 0 && (
        <>
          <HeadingMedium marginBottom={0} center>
            {label}
          </HeadingMedium>
          <Block display="flex" justifyContent="center" data-testid="document-list">
            <FileList documents={documents} align="center" />
          </Block>
        </>
      )}
    </Placeholder>
  );
};

export const DocumentsPollList: React.FC<
  TDocumentPollList & TDocumentPollListExtraArgs
> = ({
  riskProfileId,
  label,
  useDocumentsPoll,
  shouldPollDocuments,
  globalDataKey = QuestionFlowTypeKey.quote,
}) => {
  const { documents, documentsLoading } = useDocumentsPoll({
    riskProfileId,
    shouldPollDocuments,
    globalDataKey,
  });
  return (
    <StatelessDocumentList
      label={label}
      loading={documentsLoading}
      documents={documents}
    />
  );
};

export const PolicyDocumentPollList: React.FC<TDocumentPollList> = ({
  riskProfileId,
  label,
  shouldPollDocuments,
}) => (
  <DocumentsPollList
    label={label}
    riskProfileId={riskProfileId}
    useDocumentsPoll={usePolicyDocumentsPoll}
    shouldPollDocuments={shouldPollDocuments}
  />
);

export const QuoteDocumentPollList: React.FC<TDocumentPollList> = ({
  riskProfileId,
  label,
  shouldPollDocuments,
  quoteId,
  globalDataKey = QuestionFlowTypeKey.quote,
}) => {
  const [data] = useGlobal(globalDataKey);
  const documents =
    data?.riskProfile?.quotes?.find((q) => q.id === quoteId)?.documents || [];
  return documents.length > 0 ? (
    <StatelessDocumentList label={label} loading={false} documents={documents} />
  ) : (
    <DocumentsPollList
      label={label}
      riskProfileId={riskProfileId}
      useDocumentsPoll={useQuoteDocumentsPoll}
      shouldPollDocuments={shouldPollDocuments}
      globalDataKey={globalDataKey}
    />
  );
};
